import React, {useMemo} from 'react';
import _ from 'lodash';

import {
  Button,
  DropdownProps,
  Popup,
  StrictPopupProps,
} from 'semantic-ui-react';

import * as PanelSettingsViewTypes from '../state/views/panelSettings/types';
import * as PlotHelpers from '../util/plotHelpers';
import SmoothingInput from './elements/SmoothingInput';
import RangeInput from './elements/RangeInput';
import LegacyWBIcon from './elements/LegacyWBIcon';
import * as RunHelpersUtil from '../util/runhelpers';
import ModifiedDropdown from './elements/ModifiedDropdown';

import {useKeyInfoQuery} from '../state/runs/hooks';
import * as ViewHooks from '../state/views/hooks';
import * as RunSetTypes from '../state/views/runSet/types';
import * as PanelTypes from '../state/views/panel/types';
import * as PanelSettingsActions from '../state/views/panelSettings/actions';
import {useDispatch} from '../state/hooks';
import {updateConfigs} from '../state/views/panel/actions';
import {PanelWithConfig} from '../util/panels';
import * as PanelRunsLinePlot from './PanelRunsLinePlot';
import {EMPTY_SETTINGS, Settings} from '../util/panelsettings';
import makeComp from '../util/profiler';
import {SmoothingType} from '../util/math';

import {PopUpSectionTitle} from './PanelSettings.styles';

export interface LinePlotPanel
  extends PanelWithConfig<typeof PanelRunsLinePlot.PANEL_TYPE> {
  ref: PanelTypes.Ref;
}

interface PanelSettingsProps {
  popupPosition?: StrictPopupProps['position'];
  extraXAxisOptions?: string[];
  panelSettingsRef: PanelSettingsViewTypes.Ref;
  // If undefined, we don't suggest extra x-axis options (happens in
  // reports currently)
  runSetRefs?: RunSetTypes.Ref[];

  // This is an expensive hook to call! It can inspect the entire
  // panelbank config (huge if there are many metrics), or report.
  // So only call it when we need to (when the user has opened the
  // smoothing popup).
  useAllLinePlotPanels(): LinePlotPanel[];
}

const PanelSettingsComponent = makeComp(
  (props: PanelSettingsProps) => {
    const {
      popupPosition = 'bottom right',
      panelSettingsRef,
      useAllLinePlotPanels,
    } = props;

    const settings = ViewHooks.useWhole(panelSettingsRef);

    const updateSettings = ViewHooks.useViewAction(
      panelSettingsRef,
      PanelSettingsActions.update
    );

    const xAxisActive =
      settings.xAxis !== PlotHelpers.XAxisValues.Step ||
      settings.xAxisMax != null ||
      settings.xAxisMin != null;

    return (
      <>
        <Popup
          on="hover"
          hoverable
          position={popupPosition}
          trigger={
            <Button
              size="tiny"
              className={
                'wb-icon-button only-icon' +
                (xAxisActive ? ' action-button--active' : '')
              }>
              <LegacyWBIcon name="xaxis" title="X-Axis options" />
            </Button>
          }
          content={
            <>
              {props.runSetRefs != null ? (
                <XAxisPickerQueryExtra
                  runSetRefs={props.runSetRefs}
                  xAxis={settings.xAxis}
                  setXAxis={xAxis =>
                    updateSettings({xAxis, xAxisChanged: true})
                  }
                  xAxisMin={settings.xAxisMin}
                  setXAxisMin={xAxisMin => updateSettings({xAxisMin})}
                  xAxisMax={settings.xAxisMax}
                  setXAxisMax={xAxisMax => updateSettings({xAxisMax})}
                  resetXAxisToDefaults={() =>
                    updateSettings({xAxisChanged: false, xAxis: '_step'})
                  }
                />
              ) : (
                <XAxisPicker
                  extraXAxisOptions={[]}
                  loading={false}
                  xAxis={settings.xAxis}
                  setXAxis={xAxis =>
                    updateSettings({xAxis, xAxisChanged: true})
                  }
                  xAxisMin={settings.xAxisMin}
                  setXAxisMin={xAxisMin => updateSettings({xAxisMin})}
                  xAxisMax={settings.xAxisMax}
                  setXAxisMax={xAxisMax => updateSettings({xAxisMax})}
                  resetXAxisToDefaults={() =>
                    updateSettings({xAxisChanged: false, xAxis: '_step'})
                  }
                />
              )}
              {xAxisActive && (
                <div
                  onClick={() => {
                    updateSettings(EMPTY_SETTINGS);
                  }}>
                  Reset x-Axis settings
                </div>
              )}
            </>
          }
        />
        <Popup
          on="hover"
          hoverable
          position={popupPosition}
          trigger={
            <Button
              size="tiny"
              className={
                'wb-icon-button only-icon' +
                (settings.smoothingWeight > 1e-6
                  ? ' action-button--active'
                  : '')
              }>
              <LegacyWBIcon name="smooth-gray" title="Smoothing options" />
            </Button>
          }
          flowing={true}
          content={
            <SmoothingControls
              settings={settings}
              updateSettings={updateSettings}
              useAllLinePlotPanels={useAllLinePlotPanels}
            />
          }
        />
        <Popup
          on="hover"
          position={popupPosition}
          content="Ignore outliers in chart scaling"
          trigger={
            <Button
              size="tiny"
              className={
                'wb-icon-button only-icon' +
                (settings.ignoreOutliers ? ' action-button--active' : '')
              }
              onClick={e =>
                updateSettings({
                  ignoreOutliers: !settings.ignoreOutliers,
                })
              }>
              <LegacyWBIcon
                name="outlier"
                title="Ignore outliers in chart scaling"
              />
            </Button>
          }
        />
      </>
    );
  },
  {id: 'PanelSettingsComponent'}
);

export default PanelSettingsComponent;

interface XAxisPickerQueryExtraProps {
  runSetRefs: RunSetTypes.Ref[];
  xAxis: string;
  setXAxis: (xAxis: string) => void;
  xAxisMin: number | undefined;
  setXAxisMin: (xAxisMin: number | undefined) => void;
  xAxisMax: number | undefined;
  setXAxisMax: (xAxisMax: number | undefined) => void;
  resetXAxisToDefaults: () => void;
}

const SmoothingControls: React.FC<{
  settings: Settings;
  updateSettings(panelSettingsUpdate: Partial<Settings>): void;
  useAllLinePlotPanels(): LinePlotPanel[];
}> = makeComp(
  ({settings, useAllLinePlotPanels, updateSettings}) => {
    const dispatch = useDispatch();
    const linePlotPanels = useAllLinePlotPanels();
    const linePlotPanelsUsingGlobalSmoothing = linePlotPanels.filter(p => {
      const {smoothingWeight, useGlobalSmoothingWeight} = p.config;
      const smoothingWeightNotSet =
        useGlobalSmoothingWeight == null &&
        (smoothingWeight == null || smoothingWeight === 0);
      return useGlobalSmoothingWeight || smoothingWeightNotSet;
    });

    const setSmoothingForLinePlotPanels = (
      smoothingWeight: number,
      smoothingType: SmoothingType,
      forceGlobal = false
    ) => {
      const panels = forceGlobal
        ? linePlotPanels
        : linePlotPanelsUsingGlobalSmoothing;
      const refs = panels.map(p => p.ref);
      dispatch(
        updateConfigs(refs, {
          smoothingWeight,
          smoothingType,
          useGlobalSmoothingWeight: true,
        })
      );
    };
    return (
      <div className="smoothing-slider-popup">
        <div className="global-config__control">
          <div>Smoothing</div>
          <SmoothingInput
            smoothingParam={settings.smoothingWeight}
            smoothingType={settings.smoothingType}
            onChange={(smoothingParam, smoothingType) => {
              updateSettings({
                smoothingWeight: smoothingParam,
                smoothingType,
              });
              setSmoothingForLinePlotPanels(smoothingParam, smoothingType);
            }}
          />
        </div>
        {linePlotPanels.length !==
          linePlotPanelsUsingGlobalSmoothing.length && (
          <div className="use-global-smoothing-section">
            Some charts have local smoothing settings. Do you want to override
            all charts with this global smoothing setting?
            <div
              className="use-global-smoothing"
              onClick={() =>
                setSmoothingForLinePlotPanels(
                  settings.smoothingWeight,
                  settings.smoothingType,
                  true
                )
              }>
              Override all charts on this page
            </div>
          </div>
        )}
      </div>
    );
  },
  {id: 'SmoothingControls'}
);

const XAxisPickerQueryExtra = makeComp(
  (props: XAxisPickerQueryExtraProps) => {
    const {
      runSetRefs,
      xAxis,
      setXAxis,
      xAxisMin,
      setXAxisMin,
      xAxisMax,
      setXAxisMax,
      resetXAxisToDefaults,
    } = props;
    const keyInfoQuery = useKeyInfoQuery(runSetRefs);
    const extraXAxisOptions = useMemo(
      () =>
        keyInfoQuery.loading || keyInfoQuery.error != null
          ? []
          : RunHelpersUtil.globalXAxisOptions(keyInfoQuery.historyKeyInfo),
      [keyInfoQuery]
    );
    return (
      <XAxisPicker
        extraXAxisOptions={extraXAxisOptions}
        loading={keyInfoQuery.loading}
        xAxis={xAxis}
        setXAxis={setXAxis}
        xAxisMin={xAxisMin}
        setXAxisMin={setXAxisMin}
        xAxisMax={xAxisMax}
        setXAxisMax={setXAxisMax}
        resetXAxisToDefaults={resetXAxisToDefaults}
      />
    );
  },
  {id: 'XAxisPickerQueryExtra'}
);

interface XAxisPickerProps {
  loading: boolean;
  extraXAxisOptions: string[];
  xAxis: string;
  setXAxis: (xAxis: string) => void;
  xAxisMin: number | undefined;
  setXAxisMin: (xAxisMin: number | undefined) => void;
  xAxisMax: number | undefined;
  setXAxisMax: (xAxisMax: number | undefined) => void;
  resetXAxisToDefaults: () => void;
}

const XAxisPicker = makeComp(
  (props: XAxisPickerProps) => {
    const {extraXAxisOptions, loading} = props;
    const options = PlotHelpers.defaultXAxisValues
      .concat(extraXAxisOptions)
      .map(o => ({
        key: o,
        text: PlotHelpers.xAxisLabel(o),
        value: o,
      }));

    return (
      <div className="global-config-control">
        X Axis
        <ModifiedDropdown
          selection
          loading={loading}
          value={props.xAxis}
          options={options}
          onChange={(e, data: DropdownProps) => {
            props.setXAxis(data.value as string);
          }}
        />
        <PopUpSectionTitle>X Range</PopUpSectionTitle>
        <RangeInput
          onMinChange={_.debounce(newVal => {
            props.setXAxisMin(newVal);
          }, 500)}
          onMaxChange={_.debounce(newVal => {
            props.setXAxisMax(newVal);
          }, 500)}
          minValue={props.xAxisMin}
          maxValue={props.xAxisMax}
        />
      </div>
    );
  },
  {id: 'XAxisPicker'}
);
